import moment from "moment";
import * as Yup from "yup";

export const individualValidationSchema = Yup.object({
  firstName: Yup.string()
    .matches(
      /^[a-zA-Z0-9\s-'`]+$/,
      "Please enter a valid first name."
    )
    .required("First Name is required"),

  lastName: Yup.string()
    .matches(
      /^[a-zA-Z0-9\s-'`]+$/,
      "Please enter a valid last name."
    )
    .required("Last Name is required"),
  email: Yup.string()
    .required("Email is required").matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email address"
    ),
  phone: Yup.string()
    .transform((value) => value.replace(/\D/g, "")) // Remove all non-numeric characters
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits") // Check if the resulting string has exactly 10 digits
    .required("Phone Number is required"),
  socialSecurityNumber: Yup.string()
    .transform((value) => value.replace(/-/g, "")) // Remove dashes
    .matches(/^\d{9}$/, "Social Security Number must be exactly 9 digits")
    .test(
      "invalid-start",
      "SSN is not valid",
      (value) => {
        if (!value) return false;
        const prefix = value.slice(0, 3);
        return prefix !== "000" && prefix !== "666" && !value.startsWith("9");
      }
    )
    .required("Social Security Number is required"),
  dateOfBirth: Yup.date()
    .transform((value, originalValue) => {
      return originalValue === "" || isNaN(new Date(originalValue).getTime())
        ? null
        : value;
    })
    .nullable()
    .required("Date of Birth is required")
    .test("is-18", "You must be at least 18 years old", (value) => {
      if (!value) return false;
      const today = new Date();
      const cutoffDate = new Date(
        today.getFullYear() - 18,
        today.getMonth(),
        today.getDate() - 1
      );
      return new Date(value) <= cutoffDate;
    }),
  address: Yup.string().trim().required("Address is required"),
  addressLine2: Yup.string(), // Add this line (optional field)
  city: Yup.string().trim().required("City is required"),
  state: Yup.string().required("State is required"),
  ZipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, "ZIP code must be in the format 00000 or 00000-0000")
    .required("Zip Code is required."),
  documentId: Yup.string()
      .trim()
      .required("Document ID is required.")
      .when("documentType", ([documentType], schema) => {
        const typeLabels: Record<string, string> = {
          id_passport: "Passport",
          id_passport_card: "Passport Card",
          doc_green_card: "Green Card",
          id_drivers_license: "Driver’s License",
          id_drivers_permit: "Driver’s Permit",
          id_state: "State ID",
        };
        const label = typeLabels[documentType] || "selected document type";
        let min = 0;
        let max = 0;
        switch (documentType) {
          case "id_passport":
          case "id_passport_card":
            min = 6;
            max = 9;
            break;
          case "doc_green_card":
            min = 7;
            max = 9;
            break;
          case "id_drivers_license":
          case "id_drivers_permit":
          case "id_state":
            min = 4;
            max = 24;
            break;
          default:
            return schema;
        }
        return schema
          .min(min, `Document ID must be between ${min} and ${max} characters for ${label}`)
          .max(max, `Document ID must be between ${min} and ${max} characters for ${label}`);
      }),
  documentType: Yup.string().required("Govt / State issued ID is required."),
  documentState: Yup.string()
    .nullable()
    .test(
      "state-or-country",
      "Document State is required.",
      function (value) {
        const { documentCountry } = this.parent;
        return !!(value || documentCountry);
      }
    ),

  documentCountry: Yup.string()
    .nullable()
    .test(
      "country-or-state",
      "Document Country is required.",
      function (value) {
        const { documentState } = this.parent;
        return !!(value || documentState);
      }
    ),

})

// Validation schema for business form
export const businessValidationSchema = Yup.object({
  Email: Yup.string()
    .required("Business Email is required.")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email address."
    ),
  Phone: Yup.string()
    .transform((value) => value.replace(/\D/g, ""))
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits.")
    .required("Business Phone Number is required."),
  DeliveryLine: Yup.string().trim().required("Business Address is required."),
  City: Yup.string().trim().required("City is required."),
  OperatingAs: Yup.string().trim().required("Doing business as is required."),
  BusinessWebsite: Yup.string()
    .url("Must be a valid URL (e.g., https://example.com)."),
  State: Yup.string().required("State is required."),
  Country: Yup.string().required("Country is required."),
  ZipCode: Yup.string()
    .matches(
      /^\d{5}(-\d{4})?$/,
      "Zip code must be in the format 00000 or 00000-0000"
    )
    .required("Zip Code is required."),
  IdNumber: Yup.string()
    .transform((value) => value.replace(/-/g, ""))
    .matches(/^\d{9}$/, "Business EIN must be exactly 9 digits.")
    .required("Business EIN is required."),
  BusinessName: Yup.string().trim().required("Legal Business Name is required."),
  BusinessTypeId: Yup.number()
    .required("Business Type is required.")
    .notOneOf([0], "Business Type is required."),
  BusinessCategoriesId: Yup.number()
    .required("Category is required.")
    .notOneOf([0], "Category is required."),
  subCategory: Yup.number()
    .required("Sub Category is required.")
    .notOneOf([0], "Sub Category is required."),
  RegistrationDate: Yup.string()
    .nullable()
    .required("Registration Date is required.")
    .test(
      "not-in-future",
      "Registration date cannot be a future date.",
      (value) => {
        if (!value) return true; // Let the required validation handle null/empty
        return moment(value).isSameOrBefore(moment(), 'day');
      }
    ),
  RegistrationState: Yup.string()
    .nullable()
    .required("Registration State is required.")
});
